import { useEffect, useState, useCallback } from 'react';
import { useMatches } from 'react-router-dom';
import { useAIDrawer } from '../../../../hooks/useAIDrawer';
import { askOraAboutSelection } from '../../../../chatContext/chatAppContextStore';
import { resolveChatRouteId } from '../../../../chatContext/useAppContextEnvelope';
import { AIActive } from '../../../../assets/svgs/AIActive';
import { Capacitor } from '@capacitor/core';
import { useSelector } from 'react-redux';
import { selectActiveBlock } from '../../../../slices/blocks/blocksSlice';
import { capture } from '../../../../utils/analytics';

interface TooltipPosition {
  top: number;
  left: number;
}

/**
 * Where "Ask Ora AI" is offered on a text selection. Each entry is the reading
 * surface's own content container, so a selection anywhere inside it counts and
 * a selection in the chrome around it does not.
 *
 * Module scope, and exported, so the list is one shared constant rather than a
 * fresh array on every render — and so a test can render these surfaces and
 * check the selectors still find them. They are class names in other
 * components' markup; nothing here breaks when one is renamed, the feature just
 * stops working, which is how the Library reader shipped without it (ORA-459).
 */
export type EnabledSurface =
  'article' | 'video' | 'flashcard' | 'study_panel' | 'document';

export const ENABLED_SURFACES: ReadonlyArray<{
  selector: string;
  surface: EnabledSurface;
}> = [
  // Nested surfaces come before the surface they sit in: the first match wins,
  // and the study panel's document tab renders the PDF viewer inside
  // `.study-panel_body`.
  // The lecture PDF viewer's rendered pages, in both places it is mounted
  // (ChatDocumentWorkspace and StudyPanel's DocumentTabs). Only the page
  // slots: the toolbar's file name and the loading copy sit inside the same
  // viewer root and are not document text (ORA-1041).
  { selector: '[data-chat-document-viewer] [data-page]', surface: 'document' },
  // The article reader, in both places it is mounted: inside Topics, and as
  // the standalone Library reader at /library/:articleId (ORA-412).
  {
    selector: '.topics-container .article_body_center_card_content',
    surface: 'article',
  },
  {
    selector: '.library-article .article_body_center_card_content',
    surface: 'article',
  },
  { selector: '.video-player_info_header_top_title', surface: 'video' },
  {
    selector: '.video-player_info_header_description',
    surface: 'video',
  },
  { selector: '.video-page_main_topic-summary', surface: 'video' },
  {
    selector: '.flashcard_main_card_container_content',
    surface: 'flashcard',
  },
  { selector: '.study-panel_body', surface: 'study_panel' },
];

export const ENABLED_SELECTORS: string[] = ENABLED_SURFACES.map(
  ({ selector }) => selector
);

export const normalizeSelectionText = (
  text: string,
  surface: EnabledSurface
): string => (surface === 'document' ? text.replace(/\s+/g, ' ').trim() : text);

const TextSelectionTooltip: React.FC = () => {
  const { open: openAIDrawer } = useAIDrawer();
  const matches = useMatches();
  const activeBlock = useSelector(selectActiveBlock);

  // Disable the "Ask AI about selection" tooltip only while an actual
  // self-assessment block is still in progress (the live timed attempt); it
  // becomes available once the attempt is complete (reviewable), matching the
  // AI chat gate in ExamView / AIDrawerRenderer. Keyed off the block's own
  // selfAssessmentAttemptId rather than studyContext.returnTo so by-category /
  // by-system review compositions get it too.
  const isSelfAssessmentInProgress =
    Boolean(activeBlock?.selfAssessmentAttemptId) &&
    activeBlock?.selfAssessmentAccess?.isReviewable !== true;

  const [selectedText, setSelectedText] = useState('');

  const [selectedSurface, setSelectedSurface] = useState<EnabledSurface | null>(
    null
  );

  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const getEnabledSurface = useCallback(
    (element: Element | null): EnabledSurface | null => {
      if (!element) return null;

      // Check if element or any parent matches an enabled selector
      return (
        ENABLED_SURFACES.find(({ selector }) => {
          try {
            return element.closest(selector) !== null;
          } catch (e) {
            console.error(`Invalid selector: ${selector}`, e);
            return false;
          }
        })?.surface ?? null
      );
    },
    []
  );

  const shouldExcludeElement = useCallback(
    (element: Element | null): boolean => {
      if (!element) return false;

      // Traverse up the DOM tree to check if selection is within excluded areas
      let current: Element | null = element;
      while (current) {
        const classList = current.classList;

        // Exclude the left panel in QBank (question area with highlighting feature)
        if (classList.contains('left')) {
          return true;
        }

        current = current.parentElement;
      }

      return false;
    },
    []
  );

  const handleSelection = useCallback(() => {
    const selection = window.getSelection();
    const selected = selection?.toString().trim() ?? '';

    if (
      isSelfAssessmentInProgress ||
      !selected ||
      Capacitor.isNativePlatform()
    ) {
      setIsVisible(false);
      return;
    }

    const anchorNode = selection?.anchorNode;
    const focusNode = selection?.focusNode;

    const surface =
      getEnabledSurface(anchorNode?.parentElement || null) ??
      getEnabledSurface(focusNode?.parentElement || null);

    // Check if selection is within an enabled selector
    if (!surface) {
      setIsVisible(false);
      return;
    }

    // Check if selection is in excluded area
    if (
      shouldExcludeElement(anchorNode?.parentElement || null) ||
      shouldExcludeElement(focusNode?.parentElement || null)
    ) {
      setIsVisible(false);
      return;
    }

    const range = selection?.getRangeAt(0);
    const rect = range?.getBoundingClientRect();

    if (rect) {
      // Position tooltip above the selection
      const top = rect.top + window.scrollY - 50; // 50px above selection
      const left = rect.left + window.scrollX + rect.width / 2; // Center horizontally

      setSelectedText(normalizeSelectionText(selected, surface));
      setSelectedSurface(surface);
      setPosition({ top, left });
      setIsVisible(true);
    }
  }, [isSelfAssessmentInProgress, shouldExcludeElement, getEnabledSurface]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    // Hide tooltip if clicking outside and not on the tooltip itself
    if (!target.closest('.text-selection-tooltip')) {
      setIsVisible(false);
    }
  }, []);

  useEffect(() => {
    if (isSelfAssessmentInProgress) {
      setIsVisible(false);
    }
  }, [isSelfAssessmentInProgress]);

  useEffect(() => {
    // Listen for mouseup to detect text selection
    document.addEventListener('mouseup', handleSelection);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mouseup', handleSelection);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleSelection, handleClickOutside]);

  const handleAskOra = () => {
    if (selectedText && selectedSurface) {
      const target =
        resolveChatRouteId(matches) === 'chat' ? 'chat_page' : 'drawer';

      // The companion carries the passage in its envelope and seeds the
      // composer, so the student writes their question and sends it themselves
      // (ORA-1041).
      askOraAboutSelection(selectedText);

      capture('ask_ora_selection_clicked', {
        surface: selectedSurface,
        target,
      });

      if (target !== 'chat_page') {
        openAIDrawer();
      }

      setIsVisible(false);
      // Clear selection
      window.getSelection()?.removeAllRanges();
    }
  };

  if (!isVisible || !position) {
    return null;
  }

  return (
    <div
      className="text-selection-tooltip"
      // Chat's own entry point, rendered inside `#main-content`: it is chrome,
      // not something the student is reading (ORA-629).
      data-chat-snapshot="exclude"
      style={{
        position: 'absolute',
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: 'translateX(-50%)',
        zIndex: 9999,
      }}
    >
      <button
        className="button text-selection-tooltip_popover"
        onClick={handleAskOra}
      >
        <AIActive />
        <span>Ask Ora AI</span>
      </button>
    </div>
  );
};

export default TextSelectionTooltip;
